import React from "react";
import { ModalForm } from "collections/Modal";
import { Form, Input, Field, FieldProps, AddressInput } from "collections/Form";
import { object, string } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { Link, useRouteMatch, useHistory } from "polyrouter";
import { useUpdateSiteMutation } from "generated/graphql";
import { DetailsLoader, SubmitButton } from "elements";
import { protect } from "TsRoleGuards";
import { UNASSIGNED } from "Constants";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

const NewSiteSchema = object({
  siteName: string()
    .label("NAME")
    .required()
    .test(
      UNASSIGNED,
      i18n.t("ERROR_UNASSIGNED_SITE_NAME"),
      (value: string) => !nameIsUnassigned(value)
    )
    .ensure(),
  siteAddress: object({
    address1: string()
      .label("SITE_ADDRESS")
      .notRequired(),
    city: string()
      .label("CITY")
      .notRequired(),
    region: string()
      .label("STATE")
      .notRequired(),
    postalCode: string()
      .label("POSTAL_CODE")
      .notRequired(),
    country: string()
      .label("COUNTRY")
      .notRequired(),
    geohash: string()
      .label("geohash")
      .notRequired(),
    formattedAddress: string()
      .label("ADDRESS")
      .required()
  })
});

const CreateSiteModal: React.FC = () => {
  const { t } = useTranslation();
  const { url } = useRouteMatch();
  const tenantId = useTenantId();
  const [, updateSite] = useUpdateSiteMutation();
  const { push } = useHistory();
  const cancelURL = url.replace("/add", "");

  const initialValues = {
    siteName: "",
    siteAddress: {
      address1: undefined,
      city: undefined,
      region: undefined,
      postalCode: undefined,
      country: undefined,
      geohash: undefined,
      formattedAddress: ""
    }
  };

  const onSubmit = async (values: any) => {
    const siteResponse = await updateSite({
      site: {
        tenantId,
        name: values.siteName,
        address: values.siteAddress
      }
    });
    if (siteResponse.data?.upsertSite) {
      push(`/manage/sites`);
    }
  };

  return (
    <ModalForm
      title={t("ADD_SITE")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"CreateSiteModal"}
      validationSchema={NewSiteSchema}
    >
      {({ setValues, values, isSubmitting }) => (
        <Form>
          <DetailsLoader loading={isSubmitting} title={t("SAVING_SITE")}>
            <Input name="siteName" label={t("SITE_NAME")} tabIndex={1} />
            <AddressInput
              tabIndex={2}
              name="siteAddress.formattedAddress"
              placeholder={true}
              inline={true}
              onSelect={address => {
                const {
                  buildingNumber,
                  street,
                  countryName,
                  regionCode,
                  city,
                  zip,
                  geohash,
                  formattedAddress
                } = address;
                setValues({
                  ...values,
                  siteAddress: {
                    address1: street ? [buildingNumber, street].join(" ") : "",
                    country: countryName || "",
                    region: regionCode || "",
                    city: city || "",
                    postalCode: zip || "",
                    geohash: geohash || "",
                    formattedAddress: formattedAddress || ""
                  }
                });
              }}
            />
          </DetailsLoader>
          <div className="btn-container">
            <Link to={cancelURL} className="btn" data-testid="close">
              {t("CANCEL")}
            </Link>
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  isSubmitting={isSubmitting}
                  testId="save"
                  disabled={!isValid || !dirty}
                  labelKey="ADD_SITE"
                  submittingLabelKey="ADDING"
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};

export default protect("UPDATE_SITES")(CreateSiteModal);
